import { useEffect, useState } from "react";
import jsPDF from "jspdf";
import html2canvas from "html2canvas-pro";
import { Dialog } from "primereact/dialog";
import { Button } from "primereact/button";
import { ProgressSpinner } from "primereact/progressspinner";
import { makeRequest } from "app/utils/makeRequest";
import { getSessionStore } from "app/utils/sessionStore";
import { getThemeColors, isSafari } from "app/utils/utils";
import { ProposalTypes } from "app/utils/types";
import DownloadIcon from "app/assets/IconComponent/DownloadIcon";
import { ErrorPage } from "app/uicomponents/macrocomponents/ErrorPage";
import IRCIndexPage from "./IRCIndexPage";
import IRCSummaryPage from "./IRCSummaryPage";
import IRCFundingPage from "./IRCFundingPage";
import IRCProjectionChartsPage from "./IRCProjectionChartsPage";
import IRCFundingLedgerPage from "./IRCFundingLedgerPage";
import {
  adaptPFFunding,
  adaptSDFunding,
  adaptBSFunding,
  adaptLedger,
  adaptBSProjection,
  adaptProjectionLedger,
  adaptBSKPAppendix,
} from "./IRCFundingPage.adapters";
import IRCProjectionLedgerPage from "./IRCProjectionLedgerPage";
import IRCBSKPAppendixPage from "./IRCBSKPAppendixPage";
import IRCPerformancePage from "./IRCPerformancePage";
import IRCSignaturesPage from "./IRCSignaturesPage";
import IRCDisclosurePage from "./IRCDisclosurePage";
import IRCAppendixPage from "./IRCAppendixPage";
import IRCSolutionPage from "./IRCSolutionPage";

const PDF_FONT_SIZE = isSafari() ? "9.7px" : "10.7px";
const a4Width = 595.28;
const a4Height = 841.89;

const IRCPageWrapper = ({ children }: { children: React.ReactNode }) => (
  <div
    className="border-1 border-gray-500 overflow-hidden"
    style={{ aspectRatio: "1 / 1.414", fontSize: PDF_FONT_SIZE }}
  >
    {children}
  </div>
);

const addPageToPdf = async (pdf: jsPDF, pageElement: HTMLElement) => {
  const originalStyle = pageElement.style.cssText;
  pageElement.style.cssText = `
    position: fixed;
    top: -9999px;
    left: -9999px;
    width: ${a4Width}px;
    height: ${a4Height}px;
    margin: 0;
    padding: 0;
  `;
  try {
    const canvas = await html2canvas(pageElement, {
      scale: 2,
      useCORS: true,
      imageTimeout: 10000,
      logging: false,
      allowTaint: true,
      width: a4Width,
      height: a4Height,
      windowWidth: a4Width,
      windowHeight: a4Height,
    });
    const imgData = canvas.toDataURL("image/png");
    const pdfWidth = pdf.internal.pageSize.getWidth();
    const pdfHeight = pdf.internal.pageSize.getHeight();
    pdf.addImage(imgData, "PNG", 0, 0, pdfWidth, pdfHeight, undefined, "FAST");
  } finally {
    pageElement.style.cssText = originalStyle;
  }
};

interface InteractiveReviewCenterPdfDownloadProps {
  planId: string;
  selectedYear?: number;
  onClose: () => void;
}

export default function InteractiveReviewCenterPdfDownload({
  planId,
  selectedYear,
  onClose,
}: InteractiveReviewCenterPdfDownloadProps) {
  const [pdfData, setPdfData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [downloadLoading, setDownloadLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const year = selectedYear ?? getSessionStore("ircSelectedYear") ?? 1;
  const { branding } = getSessionStore("lookUpResponse");
  const { primaryColor } = getThemeColors(branding);

  useEffect(() => {
    setLoading(true);
    makeRequest("interactiveReviewCenterPdfData")
      .get(`${planId}`, true)
      .then((res: any) => {
        setPdfData(res?.data?.data);
        setLoading(false);
      })
      .catch((err: any) => {
        setError(err?.message ?? "Failed to load PDF data");
        setLoading(false);
      });
  }, [planId, year]);

  const handleDownload = async () => {
    setDownloadLoading(true);
    try {
      // eslint-disable-next-line new-cap
      const pdf = new jsPDF("p", "mm", "a4");
      const pages = document.querySelectorAll(".irc-pdf-page");
      for (let i = 0; i < pages.length; i++) {
        if (i > 0) pdf.addPage();
        // eslint-disable-next-line no-await-in-loop
        await addPageToPdf(pdf, pages[i] as HTMLElement);
      }
      pdf.save(`${pdfData?.summary?.proposalNo ?? "review-center"}.pdf`);
    } finally {
      setDownloadLoading(false);
    }
  };

  const content = () => {
    if (loading) {
      return (
        <div className="flex justify-center items-center" style={{ minHeight: "60vh" }}>
          <ProgressSpinner />
        </div>
      );
    }
    if (error) {
      return <ErrorPage errorMsg={error} height="h-17rem" />;
    }

    const { summary, funding, projection, performance, signatures, appendix, approvalDetails } =
      pdfData ?? {};

    const sessionPlanType = getSessionStore("planInfo")?.planType;

    const isBuySellOrKPI =
      sessionPlanType === ProposalTypes.BuySell ||
      sessionPlanType === ProposalTypes.KeyPersonInsurance;

    const fundingPageProps = (() => {
      switch (sessionPlanType) {
        case ProposalTypes.BuySell:
        case ProposalTypes.KeyPersonInsurance:
          return funding?.tableData?.length > 0
            ? adaptBSFunding(funding.tableData, funding.graphData ?? [], primaryColor)
            : null;

        case ProposalTypes.SplitDollar: {
          return funding?.length > 0 ? adaptSDFunding(funding, primaryColor) : null;
        }

        case ProposalTypes.PremiumFinance:
          return funding?.length > 0 ? adaptPFFunding(funding, primaryColor) : null;
        default:
          return null;
      }
    })();

    const ledgerPageProps = adaptLedger(funding?.ledger);

    const projectionPageProps =
      isBuySellOrKPI && projection?.tableData?.length > 0
        ? adaptBSProjection(projection.tableData, projection.graphData ?? [], primaryColor)
        : null;

    const projectionLedgerProps = isBuySellOrKPI ? adaptProjectionLedger(projection?.ledger) : null;

    const bsKPAppendixProps = isBuySellOrKPI ? adaptBSKPAppendix(appendix) : null;

    const sigData = signatures ?? approvalDetails ?? null;

    // Signature split: advisor counts as 1 slot; keep ≤ 5 insureds on first page
    const allInsureds = sigData?.insureds ?? [];
    const needsSignatureSplit = allInsureds.length > 5;
    const sig1Insureds = allInsureds.slice(0, 5);
    const sig2Insureds = allInsureds.slice(5);

    // Which sections exist for this plan type (regardless of whether data is present)
    const planHasFunding = [
      ProposalTypes.BuySell,
      ProposalTypes.KeyPersonInsurance,
      ProposalTypes.SplitDollar,
      ProposalTypes.PremiumFinance,
    ].includes(sessionPlanType as ProposalTypes);

    // Pre-compute page numbers (index/cover has no number; numbered pages start at 1)
    // Pages are allocated by plan-type relevance so they always appear even when blank.
    const isSplitDollar = sessionPlanType === ProposalTypes.SplitDollar;

    let pg = 0;
    const summaryPage = ++pg;
    const solutionPage = !isSplitDollar ? ++pg : null;
    const fundingChartsPage = planHasFunding ? ++pg : null;
    const fundingTablePage = planHasFunding ? ++pg : null;
    const fundingLedgerPage = planHasFunding ? ++pg : null;
    const projectionChartsPage = isBuySellOrKPI ? ++pg : null;
    const projectionTablePage = isBuySellOrKPI ? ++pg : null;
    const projectionLedgerPage = isBuySellOrKPI ? ++pg : null;
    const hasPerf = !isBuySellOrKPI;
    const postCreditChartsPage = hasPerf ? ++pg : null;
    const postCreditTablePage = hasPerf ? ++pg : null;
    const preCreditChartsPage = hasPerf ? ++pg : null;
    const preCreditTablePage = hasPerf ? ++pg : null;
    const sig1Page = sigData ? ++pg : null;
    const sig2Page = sigData && needsSignatureSplit ? ++pg : null;
    const disclosurePage = ++pg;
    const bsKPAppendixPage = isBuySellOrKPI ? ++pg : null;
    const appendixPage = !isBuySellOrKPI ? ++pg : null;

    const tocEntries = [
      { title: "Summary", number: summaryPage },
      ...(solutionPage ? [{ title: "Solution", number: solutionPage }] : []),
      ...(fundingChartsPage ? [{ title: "Funding", number: fundingChartsPage }] : []),
      ...(fundingLedgerPage ? [{ title: "Funding Ledger", number: fundingLedgerPage }] : []),
      ...(projectionChartsPage ? [{ title: "Projections", number: projectionChartsPage }] : []),
      ...(projectionLedgerPage
        ? [{ title: "Projections Ledger", number: projectionLedgerPage }]
        : []),
      ...(postCreditChartsPage
        ? [{ title: "Performance (Post-Credit)", number: postCreditChartsPage }]
        : []),
      ...(preCreditChartsPage
        ? [{ title: "Performance (Pre-Credit)", number: preCreditChartsPage }]
        : []),
      ...(sig1Page ? [{ title: "Signatures", number: sig1Page }] : []),
      { title: "Disclosure", number: disclosurePage },
      ...(bsKPAppendixPage ? [{ title: "Appendix", number: bsKPAppendixPage }] : []),
      ...(appendixPage ? [{ title: "Appendix", number: appendixPage }] : []),
    ];

    return (
      <div>
        {/* Index / Cover — no page number */}
        <IRCPageWrapper>
          <div className="irc-pdf-page h-full w-full">
            <IRCIndexPage summary={summary} pages={tocEntries} />
          </div>
        </IRCPageWrapper>
        <IRCPageWrapper>
          <div className="irc-pdf-page h-full w-full">
            <IRCSummaryPage summary={summary} pageNumber={summaryPage} />
          </div>
        </IRCPageWrapper>
        {solutionPage && (
          <IRCPageWrapper>
            <div className="irc-pdf-page h-full w-full">
              <IRCSolutionPage pageNumber={solutionPage} proposalType={summary?.proposalType} />
            </div>
          </IRCPageWrapper>
        )}

        {/* Funding pages — always rendered for plan types that have a funding section */}
        {fundingChartsPage && (
          <IRCPageWrapper>
            <div className="irc-pdf-page h-full w-full">
              <IRCFundingPage
                rows={fundingPageProps?.rows ?? []}
                columns={fundingPageProps?.columns ?? []}
                chartLabels={fundingPageProps?.chartLabels ?? []}
                chartDatasets={fundingPageProps?.chartDatasets ?? []}
                mode="charts"
                pageNumber={fundingChartsPage}
              />
            </div>
          </IRCPageWrapper>
        )}

        {fundingTablePage && (
          <IRCPageWrapper>
            <div className="irc-pdf-page h-full w-full">
              <IRCFundingPage
                rows={fundingPageProps?.rows ?? []}
                columns={fundingPageProps?.columns ?? []}
                chartLabels={fundingPageProps?.chartLabels ?? []}
                chartDatasets={fundingPageProps?.chartDatasets ?? []}
                mode="table"
                pageNumber={fundingTablePage}
                title="Business Costs"
              />
            </div>
          </IRCPageWrapper>
        )}

        {/* Funding Ledger page — always rendered for plan types that have a funding section */}
        {fundingLedgerPage && (
          <IRCPageWrapper>
            <div className="irc-pdf-page h-full w-full">
              <IRCFundingLedgerPage
                rows={ledgerPageProps?.rows ?? []}
                columns={ledgerPageProps?.columns ?? []}
                totalBenefit={ledgerPageProps?.totalBenefit ?? 0}
                totalAnnualizedPremium={ledgerPageProps?.totalAnnualizedPremium ?? 0}
                pageNumber={fundingLedgerPage}
              />
            </div>
          </IRCPageWrapper>
        )}

        {/* Projection pages — BuySell / KeyPersonInsurance only; always rendered even if blank */}
        {projectionChartsPage && (
          <IRCPageWrapper>
            <div className="irc-pdf-page h-full w-full">
              <IRCProjectionChartsPage
                chartLabels={projectionPageProps?.chartLabels ?? []}
                chartDatasets={projectionPageProps?.chartDatasets ?? []}
                pageNumber={projectionChartsPage}
              />
            </div>
          </IRCPageWrapper>
        )}
        {projectionTablePage && (
          <IRCPageWrapper>
            <div className="irc-pdf-page h-full w-full">
              <IRCFundingPage
                rows={projectionPageProps?.rows ?? []}
                columns={projectionPageProps?.columns ?? []}
                chartLabels={projectionPageProps?.chartLabels ?? []}
                chartDatasets={projectionPageProps?.chartDatasets ?? []}
                title=""
                mode="table"
                pageNumber={projectionTablePage}
              />
            </div>
          </IRCPageWrapper>
        )}

        {/* Projection Ledger — BuySell / KeyPersonInsurance only; always rendered even if blank */}
        {projectionLedgerPage && (
          <IRCPageWrapper>
            <div className="irc-pdf-page h-full w-full">
              <IRCProjectionLedgerPage
                ledger={projectionLedgerProps ?? []}
                pageNumber={projectionLedgerPage}
              />
            </div>
          </IRCPageWrapper>
        )}

        {/* Performance pages — non-BuySell/KPI only; always rendered even if blank */}
        {postCreditChartsPage && (
          <IRCPageWrapper>
            <div className="irc-pdf-page h-full w-full">
              <IRCPerformancePage
                performance={performance ?? []}
                creditType="postCredit"
                mode="charts"
                pageNumber={postCreditChartsPage}
              />
            </div>
          </IRCPageWrapper>
        )}
        {postCreditTablePage && (
          <IRCPageWrapper>
            <div className="irc-pdf-page h-full w-full">
              <IRCPerformancePage
                performance={performance ?? []}
                creditType="postCredit"
                mode="table"
                pageNumber={postCreditTablePage}
              />
            </div>
          </IRCPageWrapper>
        )}
        {preCreditChartsPage && (
          <IRCPageWrapper>
            <div className="irc-pdf-page h-full w-full">
              <IRCPerformancePage
                performance={performance ?? []}
                creditType="preCredit"
                mode="charts"
                pageNumber={preCreditChartsPage}
              />
            </div>
          </IRCPageWrapper>
        )}
        {preCreditTablePage && (
          <IRCPageWrapper>
            <div className="irc-pdf-page h-full w-full">
              <IRCPerformancePage
                performance={performance ?? []}
                creditType="preCredit"
                mode="table"
                pageNumber={preCreditTablePage}
              />
            </div>
          </IRCPageWrapper>
        )}
        {sigData && sig1Page && (
          <IRCPageWrapper>
            <div className="irc-pdf-page h-full w-full">
              <IRCSignaturesPage
                signatures={sigData}
                pageNumber={sig1Page}
                showAdvisor
                showDisclaimer
                insuredSlice={needsSignatureSplit ? sig1Insureds : allInsureds}
              />
            </div>
          </IRCPageWrapper>
        )}
        {sigData && needsSignatureSplit && sig2Page && (
          <IRCPageWrapper>
            <div className="irc-pdf-page h-full w-full">
              <IRCSignaturesPage
                signatures={sigData}
                pageNumber={sig2Page}
                showAdvisor={false}
                showDisclaimer
                insuredSlice={sig2Insureds}
              />
            </div>
          </IRCPageWrapper>
        )}
        <IRCPageWrapper>
          <div className="irc-pdf-page h-full w-full">
            <IRCDisclosurePage pageNumber={disclosurePage} />
          </div>
        </IRCPageWrapper>

        {/* Appendix — BuySell / KeyPersonInsurance; always rendered even if blank */}
        {bsKPAppendixPage && (
          <IRCPageWrapper>
            <div className="irc-pdf-page h-full w-full">
              <IRCBSKPAppendixPage
                appendixData={
                  bsKPAppendixProps ?? {
                    lifePolicies: [],
                    disabilityPolicies: [],
                    hasLivingBenefit: false,
                  }
                }
                pageNumber={bsKPAppendixPage}
              />
            </div>
          </IRCPageWrapper>
        )}

        {/* Appendix — non-BuySell/KPI; always rendered even if blank */}
        {appendixPage && (
          <IRCPageWrapper>
            <div className="irc-pdf-page h-full w-full">
              <IRCAppendixPage
                performance={performance ?? []}
                insuranceCompanyName={summary?.entity?.title}
                pageNumber={appendixPage}
              />
            </div>
          </IRCPageWrapper>
        )}
      </div>
    );
  };

  const footer = () => (
    <div className="flex justify-end gap-2 mt-2">
      <Button label="Cancel" severity="secondary" text raised onClick={onClose} />
      <Button
        onClick={handleDownload}
        raised
        disabled={loading || downloadLoading || Boolean(error)}
        style={{
          backgroundColor: primaryColor,
          color: "white",
          border: `1px solid ${primaryColor}`,
        }}
      >
        <span
          className={`w-[20px] mr-1 ${
            downloadLoading
              ? "fadeoutdown animation-duration-1000 animation-iteration-infinite mt-[-15px]"
              : ""
          }`}
        >
          <DownloadIcon />
        </span>
        {downloadLoading ? "Downloading..." : "Download"}
      </Button>
    </div>
  );

  return (
    <div className="irc-download-popup">
      <Dialog
        visible
        modal
        draggable={false}
        closeOnEscape={false}
        closable={false}
        header={<div className="conthrax-font">Preview Interactive Review Center</div>}
        footer={footer}
        contentStyle={{ backgroundColor: "white", padding: "unset", scrollbarWidth: "none" }}
        style={{ width: "50rem" }}
        onHide={onClose}
      >
        {content()}
      </Dialog>
    </div>
  );
}
